import React, { memo, useMemo } from 'react'
import { ReactComponent as CarrierIllustration } from 'assets/illustrations/CarrierIllustration.svg'
import { times } from 'lodash'
import { Table, TableHead, TableRow, TableCell, TableBody } from 'transfix-ui/components/Table'
import { Grid, makeStyles, Typography } from 'transfix-ui/core'
import Skeleton from 'transfix-ui/core/Skeleton'
import { ISourcedCarrierRate } from 'types/graphqlTypes'
import { Button, Card, ConfirmationModal, Spinner, Toast } from 'transfix-ui'
import { EditSourcedCarrierRateModal } from './EditSourcedCarrierRateModal'
import { useEditSourcedCarrierRateModal } from './EditSourcedCarrierRateModal/useEditSourcedCarrierRateModal'
import SourcedCarrierRatesTableRow from './SourcedCarrierRatesTableRow'
import { SourcedCarrierRatesQueryResult } from './graphql/SourcedCarrierRates'
import { useDeleteSourcedCarrierRate } from './hooks'

const useSourcedCarrierRatesTableStyles = makeStyles({
  card: {
    '& header': {
      width: '100%',
      '& div': {
        width: '100%',
      },
    },
  },
  cardTitle: {
    display: 'inline-flex',
    justifyContent: 'space-between',
    width: '100%',
  },
  carrierIllustration: {
    marginBottom: 10,
  },
  deleteSpinnerContainer: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
  },
})

interface ISourcedCarrierRatesTableProps {
  enableAddButton?: boolean
  loading: boolean
  refetch: SourcedCarrierRatesQueryResult['refetch']
  shipperRateId: string
  sourcedCarrierRates: ISourcedCarrierRate[]
  title: string
  dataTest?: string
}

export const SourcedCarrierRatesTable = memo<ISourcedCarrierRatesTableProps>(
  ({
    dataTest = 'sourced-rates',
    enableAddButton = false,
    loading,
    refetch,
    shipperRateId,
    sourcedCarrierRates,
    title,
  }) => {
    const classes = useSourcedCarrierRatesTableStyles()

    const {
      closeEditSourcedCarrierRateModal,
      isEditSourcedCarrierRateModalOpen,
      openEditSourcedCarrierRateModal,
      sourcedCarrierRateToEdit,
    } = useEditSourcedCarrierRateModal(refetch)

    const {
      deleteSourcedCarrierRateLoading,
      handleDeleteSourcedCarrierRate,
      modalState: { closeModal, isModalOpen, openModal },
      toastState: { closeToast, toastMessage, toastOpen, toastType },
    } = useDeleteSourcedCarrierRate(refetch)

    const skeletonRows = useMemo(() => times(5, String), [])
    const skeletonColumns = useMemo(() => times(6, String), [])

    return (
      <Card
        className={classes.card}
        dataTest={dataTest}
        title={
          <div className={classes.cardTitle}>
            <Typography display='inline' variant='subtitle1'>
              {title}
            </Typography>

            {enableAddButton && (
              <Button
                color='primary'
                onClick={() => openEditSourcedCarrierRateModal()}
                data-test='add-sourced-rate'
                className='ml1'>
                Add Sourced Rate
              </Button>
            )}
          </div>
        }>
        <Table stickyHeader={false}>
          <TableHead>
            <TableRow>
              <TableCell>Carrier</TableCell>
              <TableCell>Rate</TableCell>
              <TableCell>Capacity</TableCell>
              <TableCell>Confirmed</TableCell>
              <TableCell>Last Activity</TableCell>
              <TableCell>&nbsp;</TableCell>
              <TableCell>&nbsp;</TableCell>
            </TableRow>
          </TableHead>
          {loading ? (
            <TableBody>
              {skeletonRows.map((row, j) => (
                // eslint-disable-next-line react/no-array-index-key
                <TableRow key={`${row}-${j}`}>
                  {skeletonColumns.map((col, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <TableCell key={`${col}-${i}`}>
                      <Skeleton width='100%' />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          ) : (
            <TableBody>
              {sourcedCarrierRates?.length > 0 ? (
                sourcedCarrierRates.map(node => (
                  <SourcedCarrierRatesTableRow
                    key={node.id}
                    openDeleteSourcedCarrierRateModal={openModal}
                    openEditSourcedCarrierRateModal={openEditSourcedCarrierRateModal}
                    sourcedCarrierRate={node}
                  />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Grid container direction='column' alignItems='center' justifyContent='center'>
                      <CarrierIllustration className={classes.carrierIllustration} />

                      <Typography color='textSecondary'>No Sourced Rates</Typography>
                    </Grid>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          )}
        </Table>

        <Toast open={toastOpen} message={toastMessage} variation={toastType} onClose={closeToast} />

        <EditSourcedCarrierRateModal
          shipperRateId={shipperRateId}
          sourcedCarrierRate={sourcedCarrierRateToEdit}
          open={isEditSourcedCarrierRateModalOpen}
          onClose={closeEditSourcedCarrierRateModal}
        />

        <ConfirmationModal
          actions={[
            {
              type: 'dismiss',
              label: 'Cancel',
              action: closeModal,
              disabled: deleteSourcedCarrierRateLoading,
            },
            {
              action: () => handleDeleteSourcedCarrierRate(),
              label: 'Delete',
              type: 'delete',
              disabled: deleteSourcedCarrierRateLoading,
            },
          ]}
          open={isModalOpen}
          onClose={closeModal}>
          Are you sure that you want to delete this sourced rate?
          <br />
          {deleteSourcedCarrierRateLoading && (
            <div className={classes.deleteSpinnerContainer}>
              <Spinner position='relative' />
            </div>
          )}
        </ConfirmationModal>
      </Card>
    )
  }
)

export default SourcedCarrierRatesTable
